import React from "react";

import { EventContentArg } from "@fullcalendar/common";
import cn from "classnames";

import { ActivityStatus, DefaultActivityTypeName } from "@mapmycustomers/shared/enum/activity";

import ActivityTypeIcon from "@app/component/activity/ActivityTypeIcon";
import colors from "@app/styles/_colors.module.scss";
import getActivityStatus from "@app/util/activity/getActivityStatus";
import isFutureActivity from "@app/util/activity/isFutureActivity";
import getBadgeImage from "@app/util/badge/getBadgeImage";
import { getBadgeSvgWithColor } from "@app/util/badge/getSvgForBadge";
import { formatDate, getFormattedAddressForUi } from "@app/util/formatters";
import locatedToAddress from "@app/util/locatedToAddress";
import loggingService from "@app/util/logging";
import { parseApiDate } from "@app/util/parsers";

import EventObject from "../../type/EventObject";
import { fullCalendarViewNameToViewMode } from "../Scheduler";

import styles from "./ActivitySchedulerEvent.module.scss";

const ActivitySchedulerEvent: React.FC<EventContentArg> = ({ event, view }) => {
  if (event.display === "background" || !Object.keys(event.extendedProps).length) {
    return null;
  }
  const viewMode = fullCalendarViewNameToViewMode[view.type];
  if (!viewMode) {
    loggingService.warning(`Unknown view type in scheduler: ${view.type}. Rendering nothing.`);
    return null;
  }

  const payload = event.extendedProps as EventObject;

  if (payload.activity) {
    const isActivityOverdue = getActivityStatus(payload.activity) === ActivityStatus.OVERDUE;

    return (
      <div className={styles.container}>
        <div
          className={cn(styles.event, {
            [styles.completed]: payload.activity.completed,
            [styles.past]: !isFutureActivity(payload.activity),
          })}
        >
          <ActivityTypeIcon
            activityType={payload.activity.crmActivityType}
            isAutoEmail={!!payload.activity.emailLogId}
            tooltipProps={
              (payload.activity.account || payload.activity.contact) &&
              payload.activity.crmActivityType.name === DefaultActivityTypeName.VISIT
                ? {
                    title: getFormattedAddressForUi(
                      locatedToAddress(payload.activity.account! ?? payload.activity.contact!)
                    ),
                  }
                : undefined
            }
          />
          {payload.activity.allDay ? (
            ""
          ) : (
            <span className={styles.duration}>
              {`${formatDate(parseApiDate(payload.activity.startAt!), "p").toLowerCase()}${
                payload.activity.endAt
                  ? ` - ${formatDate(parseApiDate(payload.activity.endAt!), "p").toLowerCase()}`
                  : ""
              }`}
              &nbsp;
            </span>
          )}
          <span className={styles.name}>{payload.activity.name}</span>
        </div>
        {isActivityOverdue && (
          <div className={styles.overdueContainer}>
            {getBadgeImage(getBadgeSvgWithColor(colors.red100), 16)}
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default ActivitySchedulerEvent;
